/**
 * TEF Canada grading (D-109): the written and oral expression copies waiting for a trainer, from
 * the mock and practice exams (`productions`) and from the course (`course-productions`). The
 * trainer marks each criterion of the TEF grid of the format table (points up to its maximum, by
 * half points); the estimated score out of 699 of the section (A or B) and its NCLC (official
 * table) are computed as the server does and always shown as estimates. The AI's marks stay on
 * the server for the calibration.
 */

import {
  useAcademyCourseProductionsGradeCreate,
  useAcademyCourseProductionsList,
  useAcademyFormatRetrieve,
  useAcademyProductionsGradeCreate,
  useAcademyProductionsList,
} from "@agencyos/api-client";
import { formatScore, nclcFor, parseTefFormat, sectionEstimate, type TefFormat } from "@agencyos/core";
import { Badge, type BadgeTone, Button, EmptyState, GlassCard, GlassSheet, SectionHeader, SegmentedControl, SelectField, Skeleton, TextAreaField } from "@agencyos/ui-web";
import { ClipboardCheck } from "lucide-react";
import { type FormEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import "./ielts-admin.css";
import { localized } from "../../components/TranslatedFields";
import { fieldErrors } from "../../lib/settings";
import { dateTimeText } from "../billing/shared";
import { useAdminExam } from "./examContext";
import { AiCorrection, GradingFilters, GradingTags, GradingWarnings, useGradingFilters } from "./GradingTools";
import { type Criterion, rubricsOf } from "./GradingTab";
import { fromCourse, fromProduction, type IeltsCopy } from "./IeltsGrading";
import { ErrorList, isObject, num, Pager, str, useAcademyRoles, useRefreshAcademy } from "./shared";

type Source = "exam" | "course";
const SOURCES: Source[] = ["exam", "course"];
const STATUS_TONE: Record<string, BadgeTone> = { draft: "default", submitted: "default", graded: "success", to_trainer: "danger" };

/** 0, 0.5, 1 … max. */
function steps(max: number): number[] {
  return Array.from({ length: Math.floor(Math.max(max, 0) * 2) + 1 }, (_, i) => i / 2);
}

function previousPoints(grade: Record<string, unknown>): Record<string, { points: number | null; comment: string }> {
  const rows = Array.isArray(grade.criteria) ? (grade.criteria as unknown[]).filter(isObject) : [];
  return Object.fromEntries(rows.map((c) => [str(c.key), { points: c.points === undefined ? null : num(c.points), comment: str(c.comment) }]));
}

function useTaskLabel() {
  const { t } = useTranslation();
  return (copy: IeltsCopy) =>
    copy.source === "course"
      ? t("tef.admin.grading.courseTask", { skill: t(`tef.skill.${copy.skill}`), lesson: copy.task })
      : t("tef.admin.grading.section", { skill: t(`tef.skill.${copy.skill}`), section: copy.task === "2" ? "B" : "A" });
}

function scoreOf(copy: IeltsCopy): number | null {
  const value = copy.grade.score;
  return typeof value === "number" ? value : null;
}

export function TefGradeSheet({ copy, rubric, format, onClose }: { copy: IeltsCopy; rubric: Criterion[]; format: TefFormat; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const taskLabel = useTaskLabel();
  const examGrade = useAcademyProductionsGradeCreate({ mutation: { onSuccess: refresh } });
  const courseGrade = useAcademyCourseProductionsGradeCreate({ mutation: { onSuccess: refresh } });
  const grade = copy.source === "exam" ? examGrade : courseGrade;
  const [current, setCurrent] = useState<IeltsCopy>(copy);
  const previous = previousPoints(current.grade);
  const source = str(current.grade.source);
  const [points, setPoints] = useState<Record<string, string>>(() =>
    Object.fromEntries(rubric.map((c) => [c.key, previous[c.key]?.points !== null && previous[c.key]?.points !== undefined ? String(previous[c.key]?.points) : ""])),
  );
  const [comments, setComments] = useState<Record<string, string>>(() => Object.fromEntries(rubric.map((c) => [c.key, previous[c.key]?.comment ?? ""])));
  const [comment, setComment] = useState(str(current.grade.comment));
  const complete = rubric.length > 0 && rubric.every((c) => points[c.key] !== undefined && points[c.key] !== "");
  const total = rubric.reduce((sum, c) => sum + (Number(points[c.key]) || 0), 0);
  const max = rubric.reduce((sum, c) => sum + c.max, 0);
  const estimate = complete ? sectionEstimate(current.skill, total, max, format) : null;
  const errors = fieldErrors(grade.error);
  const current699 = scoreOf(current);
  const aiScore = typeof current.grade.ai_score === "number" ? current.grade.ai_score : null;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!complete) return;
    const data = { criteria: rubric.map((c) => ({ key: c.key, points: Number(points[c.key]), comment: comments[c.key] ?? "" })), comment };
    const onDone = (next: IeltsCopy) => setCurrent(next);
    if (copy.source === "exam") examGrade.mutate({ id: copy.id, data }, { onSuccess: (r) => r.status === 200 && onDone(fromProduction(r.data)) });
    else courseGrade.mutate({ id: copy.id, data }, { onSuccess: (r) => r.status === 200 && onDone(fromCourse(r.data)) });
  };

  return (
    <GlassSheet open title={taskLabel(current)} onClose={onClose}>
      <div className="stack">
        <div className="row">
          <Badge tone={STATUS_TONE[current.status] ?? "default"}>{t(`academyAdmin.grading.status.${current.status}`)}</Badge>
          <span className="muted">{current.learner}</span>
          <span className="muted">{dateTimeText(current.createdAt, i18n.language)}</span>
        </div>
        <section className="installment-row stack">
          <strong>{t("academyAdmin.grading.prompt")}</strong>
          <p lang="fr" className="academy-pre">
            {current.prompt}
          </p>
        </section>
        <section className="stack">
          <strong>{current.skill === "eo" ? t("academyAdmin.grading.recording") : t("academyAdmin.grading.text")}</strong>
          {current.audioUrl ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption -- its transcript is shown just below as text
            <audio controls preload="none" src={current.audioUrl} aria-label={t("academyAdmin.ielts.grading.replay")} />
          ) : null}
          {current.turns.length ? (
            <ol className="ielts-turns" lang="fr">
              {current.turns.map((turn, i) => (
                <li key={i} className={turn.role === "examiner" ? "ielts-turn ielts-turn--examiner" : "ielts-turn"}>
                  <span className="ao-footnote ao-secondary">{turn.role === "examiner" ? t("academyAdmin.ielts.grading.examiner") : t("academyAdmin.ielts.grading.candidate")}</span>
                  <span>{turn.text}</span>
                </li>
              ))}
            </ol>
          ) : current.text ? (
            <p lang="fr" className="installment-row academy-pre">
              {current.text}
            </p>
          ) : (
            <p className="muted">{t("academyAdmin.grading.noText")}</p>
          )}
          {current.skill === "ee" ? <span className="muted">{t("academyAdmin.grading.words", { count: current.words })}</span> : null}
          {str(current.grade.word_warning) ? <p className="notice-warning installment-row">{str(current.grade.word_warning)}</p> : null}
        </section>
        {current699 !== null ? (
          <section className="installment-row stack">
            <div className="row">
              <strong>{t("tef.admin.grading.current", { score: formatScore(current699, format.scaleMax), nclc: nclcFor(current.skill, current699, format.nclc) })}</strong>
              <Badge tone={source === "ai" ? "offer" : "success"}>{source === "ai" ? t("academyAdmin.grading.byAi") : t("academyAdmin.grading.byTrainer")}</Badge>
              {aiScore !== null && source !== "ai" ? <span className="muted">{t("tef.admin.grading.ai", { score: formatScore(aiScore, format.scaleMax) })}</span> : null}
            </div>
            <p className="muted">{t("tef.estimate")}</p>
          </section>
        ) : null}

        <AiCorrection grade={current.grade} />
        <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("academyAdmin.grading.form")}>
          <h3 className="ao-headline">{t("tef.admin.grading.rubric", { skill: t(`tef.skill.${current.skill}`) })}</h3>
          {rubric.length ? null : <p className="ko">{t("academyAdmin.grading.noRubric")}</p>}
          <fieldset className="plain-fieldset stack" disabled={!canEdit}>
            {rubric.map((c) => (
              <div key={c.key} className="installment-row ielts-criterion">
                <SelectField
                  label={t("tef.admin.grading.criterion", { label: localized(c.label, i18n.language) || c.key, max: c.max })}
                  value={points[c.key] ?? ""}
                  options={[{ value: "", label: t("academyAdmin.ielts.grading.pick") }, ...steps(c.max).map((p) => ({ value: String(p), label: String(p).replace(".", i18n.language.startsWith("fr") ? "," : ".") }))]}
                  onChange={(e) => setPoints((all) => ({ ...all, [c.key]: e.target.value }))}
                />
                <TextAreaField
                  label={t("academyAdmin.grading.criterionComment")}
                  rows={2}
                  value={comments[c.key] ?? ""}
                  onChange={(e) => setComments((all) => ({ ...all, [c.key]: e.target.value }))}
                />
              </div>
            ))}
            <TextAreaField label={t("academyAdmin.grading.comment")} rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
            <div className="ielts-band-total" aria-live="polite">
              <span className="ao-footnote ao-secondary">{t("tef.admin.grading.computed", { points: total, max })}</span>
              <strong className="big-number ao-num">{formatScore(estimate, format.scaleMax)}</strong>
              <span className="muted">{estimate !== null ? t("tef.nclcEstimate", { n: nclcFor(current.skill, estimate, format.nclc) }) : t("tef.estimate")}</span>
            </div>
            {errors.criteria ? (
              <p role="alert" className="ao-field__error">
                {errors.criteria}
              </p>
            ) : (
              <ErrorList error={grade.error} />
            )}
            <div className="row">
              <Button type="submit" disabled={grade.isPending || !complete}>
                {grade.isPending ? t("common.loading") : t("academyAdmin.grading.grade")}
              </Button>
              {grade.isSuccess ? (
                <span role="status" className="ok">
                  {t("academyAdmin.grading.graded")}
                </span>
              ) : null}
            </div>
          </fieldset>
        </form>
      </div>
    </GlassSheet>
  );
}

export function TefGradingTab() {
  const { t, i18n } = useTranslation();
  const { params } = useAdminExam();
  const taskLabel = useTaskLabel();
  const [source, setSource] = useState<Source>("exam");
  const grading = useGradingFilters();
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<IeltsCopy | null>(null);
  const close = useCallback(() => setOpen(null), []);
  const formatQuery = useAcademyFormatRetrieve(params);
  const formatData = formatQuery.data?.status === 200 ? formatQuery.data.data : undefined;
  const rubrics = useMemo(() => rubricsOf(formatData?.rubrics), [formatData]);
  const format = useMemo(() => parseTefFormat(formatData), [formatData]);
  const filters = { ...params, ...grading.params, page };
  const exam = useAcademyProductionsList(filters, { query: { enabled: source === "exam" } });
  const course = useAcademyCourseProductionsList(filters, { query: { enabled: source === "course" } });
  const examData = exam.data?.status === 200 ? exam.data.data : undefined;
  const courseData = course.data?.status === 200 ? course.data.data : undefined;
  const data = source === "exam" ? examData : courseData;
  const rows: IeltsCopy[] = source === "exam" ? (examData?.results ?? []).filter((p) => p.skill === "ee" || p.skill === "eo").map(fromProduction) : (courseData?.results ?? []).map(fromCourse);

  return (
    <GlassCard as="section" className="stack" aria-labelledby="tef-grading-title">
      <SectionHeader id="tef-grading-title" title={t("tef.admin.grading.title")} subtitle={t("tef.admin.grading.intro")} />
      <GradingWarnings />
      <div className="ielts-toolbar">
        <SegmentedControl
          label={t("academyAdmin.ielts.grading.source")}
          value={source}
          options={SOURCES.map((s) => ({ value: s, label: t(`academyAdmin.ielts.grading.sources.${s}`) }))}
          onChange={(value) => {
            setSource(value);
            setPage(1);
          }}
        />
        <GradingFilters
          values={grading.values}
          onChange={(next) => {
            grading.setValues(next);
            setPage(1);
          }}
        />
      </div>
      {data === undefined ? (
        <Skeleton style={{ height: 200 }} />
      ) : rows.length ? (
        <>
          <p className="muted">{t("academyAdmin.count", { count: data.count })}</p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.grading.learner")}</th>
                  <th>{t("academyAdmin.grading.task")}</th>
                  <th>{t("academyAdmin.grading.date")}</th>
                  <th>{t("academyAdmin.grading.wordCount")}</th>
                  <th>{t("tef.admin.grading.score")}</th>
                  <th>{t("academyAdmin.statusLabel")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((copy) => {
                  const by = str(copy.grade.source);
                  return (
                    <tr key={copy.id}>
                      <td>
                        <button type="button" className="link" onClick={() => setOpen(copy)}>
                          {copy.learner}
                        </button>
                      </td>
                      <td className="ielts-nowrap">{taskLabel(copy)}</td>
                      <td className="ielts-nowrap">{dateTimeText(copy.createdAt, i18n.language)}</td>
                      <td className="ao-num">{copy.skill === "ee" ? copy.words : "—"}</td>
                      <td className="ielts-nowrap">
                        <span className="ao-num">{formatScore(scoreOf(copy), format.scaleMax)}</span>
                        {by ? <span className="muted"> · {by === "ai" ? t("academyAdmin.grading.byAi") : t("academyAdmin.grading.byTrainer")}</span> : null}
                        <GradingTags row={copy} />
                      </td>
                      <td>
                        <Badge tone={STATUS_TONE[copy.status] ?? "default"}>{t(`academyAdmin.grading.status.${copy.status}`)}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
        </>
      ) : (
        <EmptyState compact icon={<ClipboardCheck size={22} strokeWidth={1.75} />} title={t(`academyAdmin.grading.empty.${grading.values.status}`)} />
      )}
      {open ? <TefGradeSheet key={`${open.source}-${open.id}`} copy={open} rubric={rubrics[open.skill] ?? []} format={format} onClose={close} /> : null}
    </GlassCard>
  );
}
